import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Calendar, Clock, Play, Loader2, AlertCircle, Ticket, Film } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LivePaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);
  const [pollingStatus, setPollingStatus] = useState('Checking payment status...');
  const [isVodPurchase, setIsVodPurchase] = useState(false);

  // Poll VOD payment status
  const pollVodPaymentStatus = useCallback(async (sessionId, eventSlug, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please check your email for confirmation or contact support.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/livestream/payment/vod-status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setVerified(true);
        setPollingStatus('Payment verified!');
        
        // Fetch event details
        const eventsResponse = await axios.get(`${API}/livestream/events`);
        const foundEvent = eventsResponse.data.events.find(e => e.slug === eventSlug);
        
        if (foundEvent) {
          setEvent(foundEvent);
        }
        setLoading(false);
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setLoading(false);
        return;
      }

      setPollingStatus(`Verifying payment... (attempt ${attempts + 1}/${maxAttempts})`);
      setTimeout(() => pollVodPaymentStatus(sessionId, eventSlug, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking VOD payment status:', err);
      setTimeout(() => pollVodPaymentStatus(sessionId, eventSlug, attempts + 1), pollInterval);
    }
  }, []);

  // Poll payment status from backend
  const pollPaymentStatus = useCallback(async (sessionId, eventId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please check your email for confirmation or contact support.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/livestream/payment/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setVerified(true);
        setPollingStatus('Payment verified!');
        
        // Fetch event details
        const eventsResponse = await axios.get(`${API}/livestream/events`);
        const foundEvent = eventsResponse.data.events.find(e => e.id === eventId);
        
        if (foundEvent) {
          setEvent(foundEvent);
        }
        setLoading(false);
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setLoading(false);
        return;
      }

      // Continue polling
      setPollingStatus(`Verifying payment... (attempt ${attempts + 1}/${maxAttempts})`);
      setTimeout(() => pollPaymentStatus(sessionId, eventId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      
      // If status endpoint fails, fall back to legacy verify endpoint
      if (attempts === 0) {
        await fallbackVerifyPayment(sessionId, eventId);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, eventId, attempts + 1), pollInterval);
      }
    }
  }, []);

  // Fallback to legacy verification method
  const fallbackVerifyPayment = async (sessionId, eventId) => {
    const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
    
    if (!customer.id) {
      localStorage.setItem('pendingPayment', JSON.stringify({ sessionId, eventId }));
      navigate('/live/login');
      return;
    }

    try {
      await axios.post(
        `${API}/livestream/payment/verify-payment?session_id=${sessionId}&event_id=${eventId}&customer_id=${customer.id}`
      );
      setVerified(true);

      // Get event details
      const eventsResponse = await axios.get(`${API}/livestream/events`);
      const foundEvent = eventsResponse.data.events.find(e => e.id === eventId);
      
      if (foundEvent) {
        setEvent(foundEvent);
      } else {
        setError('Event not found');
      }
    } catch (err) {
      console.error('Verification error:', err);
      if (err.response?.data?.detail?.includes('already have a ticket')) {
        setVerified(true);
        const eventsResponse = await axios.get(`${API}/livestream/events`);
        const foundEvent = eventsResponse.data.events.find(e => e.id === eventId);
        if (foundEvent) setEvent(foundEvent);
      } else {
        setError(err.response?.data?.detail || 'Payment verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const eventId = searchParams.get('event_id');
    const isVod = searchParams.get('vod') === 'true';
    const eventSlug = searchParams.get('event_slug');
    
    setIsVodPurchase(isVod);

    // For VOD purchases
    if (isVod && sessionId && eventSlug) {
      const token = localStorage.getItem('livestreamToken');
      const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
      
      if (!token || !customer.id) {
        localStorage.setItem('pendingPayment', JSON.stringify({ sessionId, eventSlug, isVod: true }));
        navigate('/live/login');
        return;
      }

      // Handle free VOD purchase
      if (sessionId.startsWith('free_vod_')) {
        setVerified(true);
        setLoading(false);
        // Fetch event details
        axios.get(`${API}/livestream/events`).then(res => {
          const foundEvent = res.data.events.find(e => e.slug === eventSlug);
          if (foundEvent) setEvent(foundEvent);
        });
        return;
      }

      pollVodPaymentStatus(sessionId, eventSlug);
      return;
    }

    // For ticket purchases
    if (!sessionId || !eventId) {
      setError('Invalid payment session');
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('livestreamToken');
    const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
    
    if (!token || !customer.id) {
      localStorage.setItem('pendingPayment', JSON.stringify({ sessionId, eventId }));
      navigate('/live/login');
      return;
    }

    // Handle free ticket
    if (sessionId.startsWith('free_')) {
      setVerified(true);
      setLoading(false);
      // Fetch event details
      axios.get(`${API}/livestream/events`).then(res => {
        const foundEvent = res.data.events.find(e => e.id === eventId);
        if (foundEvent) setEvent(foundEvent);
      });
      return;
    }

    // Start polling for payment status
    pollPaymentStatus(sessionId, eventId);
  }, [searchParams, navigate, pollPaymentStatus, pollVodPaymentStatus]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-red-500 mx-auto mb-4" />
          <p className="text-white text-lg">Verifying your payment...</p>
          <p className="text-gray-400 text-sm mt-2">{pollingStatus}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <Card className="bg-[#111111] border-[#222222] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Payment Error</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <Button 
              className="w-full bg-red-500 hover:bg-red-600"
              onClick={() => navigate('/live')}
            >
              Back to Events
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-[#333333] overflow-hidden">
          {/* Green Success Banner */}
          <div className="bg-gradient-to-r from-green-600 to-green-500 px-8 py-6 text-center">
            <CheckCircle2 className="h-16 w-16 text-white mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-green-100">
              {isVodPurchase ? 'Your recording is ready to watch' : 'Thank you for your purchase'}
            </p>
          </div>

          <CardContent className="p-8">
            {/* Ticket/VOD Confirmation */}
            <div className="bg-[#0a0a0a]/30 rounded-xl p-6 mb-6 border border-[#333333]">
              <div className="flex items-center gap-3 mb-4">
                {isVodPurchase ? (
                  <>
                    <Film className="h-6 w-6 text-purple-500" />
                    <span className="text-gray-400">Your Recording</span>
                  </>
                ) : (
                  <>
                    <Ticket className="h-6 w-6 text-red-500" />
                    <span className="text-gray-400">Your Ticket</span>
                  </>
                )}
              </div>
              
              {event && (
                <>
                  <h2 className="text-2xl font-bold text-white mb-4">{event.title}</h2>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-[#1a1a1a]/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-gray-400 mb-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm">{isVodPurchase ? 'Event Date' : 'Date'}</span>
                      </div>
                      <p className="text-white font-medium">{formatDate(event.event_date)}</p>
                    </div>
                    <div className="bg-[#1a1a1a]/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-gray-400 mb-1">
                        {isVodPurchase ? (
                          <>
                            <Film className="h-4 w-4" />
                            <span className="text-sm">Type</span>
                          </>
                        ) : (
                          <>
                            <Clock className="h-4 w-4" />
                            <span className="text-sm">Time</span>
                          </>
                        )}
                      </div>
                      <p className="text-white font-medium">
                        {isVodPurchase ? 'On-Demand Recording' : formatTime(event.event_date)}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-400 text-sm">{event.description}</p>

                  {!isVodPurchase && event.voting_enabled && (
                    <div className="mt-4 bg-purple-900/30 border border-purple-700/50 rounded-lg p-3">
                      <p className="text-purple-300 text-sm flex items-center gap-2">
                        <span>Vote icon</span>
                        <span>This event includes <strong>Live Voting</strong> - participate during the stream!</span>
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-bold text-white mb-3">
                {isVodPurchase ? 'How to Watch Your Recording' : 'How to Watch'}
              </h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                {isVodPurchase ? (
                  <>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Click "Watch Recording" to start watching immediately</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>You can watch the recording unlimited times</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Access your recordings anytime from "My Account"</span>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Click "Continue to Watch" to access your live stream</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>The stream will be available at the scheduled time</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>You can access your ticket anytime from "My Tickets"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-400 mt-1">⚠</span>
                      <span><strong>Important:</strong> Only one device can watch at a time for security</span>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {isVodPurchase ? (
                <>
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white text-lg py-6"
                    onClick={() => navigate(`/live/watch/${event?.slug}`)}
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Watch Recording
                  </Button>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant="outline"
                      className="border-[#333333] text-white hover:bg-[#1a1a1a]"
                      onClick={() => navigate('/live/account')}
                    >
                      <Film className="h-4 w-4 mr-2" />
                      My Account
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-[#333333] text-white hover:bg-[#1a1a1a]"
                      onClick={() => navigate('/live')}
                    >
                      Browse More Events
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Button 
                    className="w-full bg-red-500 hover:bg-red-600 text-white text-lg py-6"
                    onClick={() => navigate(`/live/watch/${event?.slug}`)}
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Continue to Watch Stream
                  </Button>
                  
                  <div className={`grid ${event?.restricted_navigation ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                    <Button 
                      variant="outline"
                      className="border-[#333333] text-white hover:bg-[#1a1a1a]"
                      onClick={() => navigate('/live/my-tickets')}
                    >
                      <Ticket className="h-4 w-4 mr-2" />
                      My Tickets
                    </Button>
                    {/* Hide Browse More Events for restricted events */}
                    {!event?.restricted_navigation && (
                      <Button 
                        variant="outline"
                        className="border-[#333333] text-white hover:bg-[#1a1a1a]"
                        onClick={() => navigate('/live')}
                      >
                        Browse More Events
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer Note */}
        <p className="text-center text-gray-500 text-sm mt-6">
          A confirmation has been sent to your email. Keep this page bookmarked for easy access.
        </p>
      </div>
    </div>
  );
};

export default LivePaymentSuccess;
